// =============================================================================
// SIREPPAR - Check de aprobación de una sección de documentos
// =============================================================================
// El mismo check verde que llevan los tipos en /documentos, pero para una sección
// que abarca VARIOS tipos: los DNI son frente + dorso, y la supervisora decide una
// sola cosa ("los DNI de las autoridades están"), no dos.
//
// El estado combinado es el más conservador: la sección está aprobada sólo si TODAS
// sus filas lo están. Media sección aprobada no es un estado que alguien haya
// querido dejar, así que no se muestra como aprobada.
// =============================================================================

import toast from 'react-hot-toast';

export type EstadoRevisionSeccion = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

export interface FilaSeccion {
  tipo: string;
  admisibilidad: { estado: EstadoRevisionSeccion; revisadoPorNombre: string | null; revisadoEn: string | null };
  analisis: { estado: EstadoRevisionSeccion; revisadoPorNombre: string | null; revisadoEn: string | null };
  nota: string | null;
}

interface Props {
  /** Filas del expediente que componen esta sección (una por tipo). */
  filas: FilaSeccion[];
  dimension: 'ADMISIBILIDAD' | 'ANALISIS';
  /** Archivos de la sección sin revisar EN ESA dimensión, para el aviso. */
  sinRevisar: number;
  puedeEditar: boolean;
  onCambiar: (estado: EstadoRevisionSeccion) => void;
}

/** Estado combinado de la sección. Aprobada sólo si lo están todas sus filas. */
export function estadoDeSeccion(
  filas: FilaSeccion[],
  dimension: 'ADMISIBILIDAD' | 'ANALISIS',
): EstadoRevisionSeccion | null {
  if (filas.length === 0) return null;
  const estados = filas.map((f) => (dimension === 'ADMISIBILIDAD' ? f.admisibilidad : f.analisis).estado);
  if (estados.some((e) => e === 'RECHAZADO')) return 'RECHAZADO';
  return estados.every((e) => e === 'APROBADO') ? 'APROBADO' : 'PENDIENTE';
}

/** Quién y cuándo la revisó. Se toma de la primera fila firmada. */
export function firmaDeSeccion(
  filas: FilaSeccion[],
  dimension: 'ADMISIBILIDAD' | 'ANALISIS',
): { por: string | null; en: string | null } {
  for (const f of filas) {
    const d = dimension === 'ADMISIBILIDAD' ? f.admisibilidad : f.analisis;
    if (d.revisadoPorNombre) return { por: d.revisadoPorNombre, en: d.revisadoEn };
  }
  return { por: null, en: null };
}

export default function CheckSeccionTipo({ filas, dimension, sinRevisar, puedeEditar, onCambiar }: Props) {
  const estado = estadoDeSeccion(filas, dimension);
  if (estado === null) return null;

  // Sin permiso de edición el check se muestra IGUAL, sólo que deshabilitado. Antes
  // devolvía null y la vista del partido perdía la casilla: la misma sección se veía
  // con una forma distinta según quién mirara, y no había cómo saber de un vistazo si
  // la Secretaría ya la había dado por completa. Sin tildar queda el cuadrado blanco.
  if (!puedeEditar) {
    return (
      // NO va `disabled`: el navegador pinta gris un check deshabilitado y le gana al
      // accent-color, así que la sección aprobada se veía apagada en vez de verde.
      // `readOnly` + `pointer-events-none` lo dejan igual de inerte —el clic pasa de
      // largo al encabezado— y conserva el verde.
      <input
        type="checkbox"
        checked={estado === 'APROBADO'}
        readOnly
        title={estado === 'APROBADO'
          ? 'La Secretaría Electoral dio esta sección por completa'
          : 'La Secretaría Electoral todavía no dio esta sección por completa'}
        className="w-5 h-5 flex-shrink-0 rounded border-gray-300 bg-white accent-green-600 text-green-600
          pointer-events-none"
      />
    );
  }

  return (
    <input
      type="checkbox"
      checked={estado === 'APROBADO'}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const aprobar = e.target.checked;
        // NO se aprueba con archivos sin revisar. El check declara un juicio y no se
        // puede juzgar lo que nadie abrió. El backend lo rechaza igual (409); acá se
        // avisa antes para no gastar el viaje y decir exactamente cuántos faltan.
        if (aprobar && sinRevisar > 0) {
          toast.error(
            `No es posible aprobar: quedan ${sinRevisar} ` +
            `${sinRevisar === 1 ? 'documento pendiente' : 'documentos pendientes'} dentro.`,
          );
          e.target.checked = false;
          return;
        }
        onCambiar(aprobar ? 'APROBADO' : 'PENDIENTE');
      }}
      title={estado === 'APROBADO'
        ? 'Desmarcar: la sección vuelve a pendiente'
        : sinRevisar > 0
          ? `Quedan ${sinRevisar} documento(s) pendientes dentro`
          : 'Aprobar la sección completa'}
      className="w-5 h-5 flex-shrink-0 rounded border-gray-300 accent-green-600 text-green-600 focus:ring-green-500 cursor-pointer"
    />
  );
}
